import React, { useState, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { useIsMobile } from '@/lib/useIsMobile'
import { formatRelativeDate } from '@/lib/utils'
import { Plus, Search, X, Wrench, Globe, Phone, Trash2, ArrowUpDown, Check, Shield, AlertTriangle } from 'lucide-react'
import { toast } from 'sonner'
import Avatar from '@/components/Avatar'

const PAYMENT_TERMS = ['net15', 'net30', 'net45', 'net60', 'due_on_receipt']
const PAYMENT_TERM_LABEL: Record<string, string> = {
  net15: 'Net 15',
  net30: 'Net 30',
  net45: 'Net 45',
  net60: 'Net 60',
  due_on_receipt: 'Due on receipt',
}
const SORT_OPTIONS = [
  { value: 'created_at_desc', label: 'Newest first' },
  { value: 'created_at_asc', label: 'Oldest first' },
  { value: 'name_asc', label: 'Name A→Z' },
  { value: 'name_desc', label: 'Name Z→A' },
]
const STATUS_FILTERS = ['all', 'active', 'inactive', 'on-hold']
const STATUS_BADGE: Record<string, { color: string; bg: string; label: string }> = {
  active:    { color: 'var(--positive)',      bg: 'var(--positive-dim)',  label: 'Active' },
  inactive:  { color: 'var(--ink-secondary)', bg: 'var(--border-subtle)', label: 'Inactive' },
  'on-hold': { color: 'var(--warning)',       bg: 'var(--warning-dim)',   label: 'On hold' },
}

function NewVendorModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const { workspace } = useAuth()
  const [form, setForm] = useState({
    name: '', email: '', phone: '', website: '',
    status: 'active', payment_terms: 'net30', notes: '',
    address_line1: '', address_line2: '',
    city: '', state: '', zip: '', country: '',
  })
  const [saving, setSaving] = useState(false)
  const set = (k: string, v: string) => setForm(p => ({ ...p, [k]: v }))
  const inp: React.CSSProperties = { width: '100%', padding: '9px 12px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box' }

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Vendor name is required'); return }
    setSaving(true)
    const { data, error } = await supabase.from('vendors').insert({
      name: form.name,
      email: form.email || null,
      phone: form.phone || null,
      website: form.website || null,
      notes: form.notes || null,
      status: form.status,
      payment_terms: form.payment_terms,
      address_line1: form.address_line1 || null,
      address_line2: form.address_line2 || null,
      city: form.city || null,
      state: form.state || null,
      zip: form.zip || null,
      country: form.country || null,
      workspace_id: workspace!.id,
    }).select().single()
    setSaving(false)
    if (error) { toast.error(`Error: ${error.message}`); return }
    toast.success('Vendor created')
    onCreated(data.id)
    onClose()
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, background: 'var(--overlay-scrim)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 24 }}
      onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.15 }}
        onClick={e => e.stopPropagation()}
        style={{ background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 20, padding: 28, width: '100%', maxWidth: 500, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexShrink: 0 }}>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--ink-primary)', margin: 0 }}>New vendor</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0 }}><X style={{ width: 18, height: 18 }} /></button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, overflowY: 'auto', minHeight: 0 }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Vendor name *</label>
            <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Reliable HVAC Co." style={inp}
              onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
              onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Email</label>
              <input value={form.email} onChange={e => set('email', e.target.value)} placeholder="ops@reliable.com" style={inp}
                onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
                onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Phone</label>
              <input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+1 (555) 000-0000" style={inp}
                onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
                onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
            </div>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Website</label>
            <input value={form.website} onChange={e => set('website', e.target.value)} placeholder="https://reliable.com" style={inp}
              onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
              onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Status</label>
              <select value={form.status} onChange={e => set('status', e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="on-hold">On hold</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Payment terms</label>
              <select value={form.payment_terms} onChange={e => set('payment_terms', e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
                {PAYMENT_TERMS.map(t => <option key={t} value={t}>{PAYMENT_TERM_LABEL[t]}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Notes</label>
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Anything worth remembering..." rows={2}
              style={{ ...inp, resize: 'none', lineHeight: 1.5 }}
              onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
              onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
          </div>
          <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: 0 }}>Address (optional)</p>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Address line 1</label>
              <input value={form.address_line1} onChange={e => set('address_line1', e.target.value)} placeholder="123 Main St" style={inp}
                onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
                onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Address line 2</label>
              <input value={form.address_line2} onChange={e => set('address_line2', e.target.value)} placeholder="Suite 4B (optional)" style={inp}
                onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
                onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12 }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>City</label>
                <input value={form.city} onChange={e => set('city', e.target.value)} placeholder="New York" style={inp}
                  onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
                  onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>State</label>
                <input value={form.state} onChange={e => set('state', e.target.value)} placeholder="NY" style={inp}
                  onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
                  onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>ZIP</label>
                <input value={form.zip} onChange={e => set('zip', e.target.value)} placeholder="10001" style={inp}
                  onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
                  onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
              </div>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Country</label>
              <input value={form.country} onChange={e => set('country', e.target.value)} placeholder="US" style={inp}
                onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
                onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 24, flexShrink: 0 }}>
          <button onClick={onClose} className="btn-ghost" style={{ flex: 1, justifyContent: 'center' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} className="btn-primary" style={{ flex: 1, justifyContent: 'center' }}>
            {saving ? 'Creating...' : 'Create vendor'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  )
}

function VendorCard({ vendor, serviceLineCount, onClick, onDelete }: any) {
  const statusBadge = STATUS_BADGE[vendor.status || 'active'] || STATUS_BADGE.active
  const insExpiresAt = vendor.insurance_expires_at ? new Date(vendor.insurance_expires_at) : null
  const insExpired = insExpiresAt && insExpiresAt < new Date()
  const insExpiringSoon = insExpiresAt && !insExpired && insExpiresAt < new Date(Date.now() + 30 * 86400000)

  return (
    <motion.div whileHover={{ y: -2 }} onClick={onClick}
      style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 16, padding: '18px 20px', cursor: 'pointer', transition: 'border-color 200ms ease' }}
      onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent-border)'}
      onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-subtle)'}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Avatar name={vendor.name} size={40} variant="company" />
          <div>
            <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-primary)', margin: '0 0 3px', letterSpacing: '-0.01em' }}>{vendor.name}</h3>
            {vendor.website && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Globe style={{ width: 10, height: 10, color: 'var(--ink-tertiary)' }} />
                <span style={{ fontSize: 11, color: 'var(--ink-tertiary)' }}>{vendor.website.replace(/^https?:\/\//, '')}</span>
              </div>
            )}
          </div>
        </div>
        <button onClick={e => { e.stopPropagation(); onDelete() }}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 4, display: 'flex', borderRadius: 6, transition: 'all 150ms ease' }}
          onMouseEnter={e => { e.currentTarget.style.color = 'var(--danger)'; e.currentTarget.style.background = 'color-mix(in srgb, var(--danger) var(--status-tint-dim), transparent)' }}
          onMouseLeave={e => { e.currentTarget.style.color = 'var(--ink-tertiary)'; e.currentTarget.style.background = 'none' }}>
          <Trash2 style={{ width: 13, height: 13 }} />
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 14 }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: statusBadge.color, background: statusBadge.bg, padding: '2px 8px', borderRadius: 100, width: 'fit-content' }}>
            {statusBadge.label}
          </span>
          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--accent)', background: 'var(--accent-dim)', border: '1px solid color-mix(in srgb, var(--accent) var(--status-tint-strong), transparent)', padding: '2px 8px', borderRadius: 100, width: 'fit-content' }}>
            {PAYMENT_TERM_LABEL[vendor.payment_terms || 'net30'] || vendor.payment_terms}
          </span>
          {insExpired && (
            <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--danger)', background: 'var(--danger-dim)', border: '1px solid color-mix(in srgb, var(--danger) var(--status-tint-strong), transparent)', padding: '2px 8px', borderRadius: 100, display: 'flex', alignItems: 'center', gap: 4, width: 'fit-content' }}>
              <AlertTriangle style={{ width: 10, height: 10 }} /> Insurance expired
            </span>
          )}
          {insExpiringSoon && (
            <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--warning)', background: 'var(--warning-dim)', border: '1px solid color-mix(in srgb, var(--warning) var(--status-tint-strong), transparent)', padding: '2px 8px', borderRadius: 100, display: 'flex', alignItems: 'center', gap: 4, width: 'fit-content' }}>
              <AlertTriangle style={{ width: 10, height: 10 }} /> Expiring soon
            </span>
          )}
        </div>
        {vendor.phone && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <Phone style={{ width: 11, height: 11, color: 'var(--ink-tertiary)' }} />
            <span style={{ fontSize: 12, color: 'var(--ink-secondary)' }}>{vendor.phone}</span>
          </div>
        )}
        {vendor.license_number && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <Shield style={{ width: 11, height: 11, color: 'var(--ink-tertiary)' }} />
            <span style={{ fontSize: 12, color: 'var(--ink-secondary)' }}>License {vendor.license_number}</span>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 12, borderTop: '1px solid var(--border-subtle)' }}>
        <span style={{ fontSize: 11, color: 'var(--ink-tertiary)' }}>
          {serviceLineCount === 0 ? 'No service lines' : `${serviceLineCount} service line${serviceLineCount !== 1 ? 's' : ''}`}
        </span>
        <span style={{ fontSize: 11, color: 'var(--ink-tertiary)' }}>{formatRelativeDate(vendor.updated_at)}</span>
      </div>
    </motion.div>
  )
}

export default function Vendors() {
  const { workspace } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const isMobile = useIsMobile()
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [sort, setSort] = useState('created_at_desc')
  const [showNewModal, setShowNewModal] = useState(false)
  const [showSortMenu, setShowSortMenu] = useState(false)
  const [view, setView] = useState<'grid' | 'list'>('grid')

  const { data: vendors = [], isLoading } = useQuery({
    queryKey: ['vendors', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data, error } = await supabase.from('vendors').select('*').eq('workspace_id', workspace.id).order('created_at', { ascending: false })
      if (error) throw error
      return data || []
    },
    enabled: !!workspace?.id,
  })

  const { data: vendorServiceLines = [] } = useQuery({
    queryKey: ['vendor-service-lines-counts', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data } = await supabase.from('vendor_service_lines').select('vendor_id, service_line_id')
      return data || []
    },
    enabled: !!workspace?.id,
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('vendors').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['vendors'] }); toast.success('Vendor deleted') },
    onError: () => toast.error('Failed to delete'),
  })

  const getServiceLineCount = (vendor: any) => vendorServiceLines.filter((vsl: any) => vsl.vendor_id === vendor.id).length

  const filtered = useMemo(() => {
    let list = [...vendors]
    if (statusFilter !== 'all') list = list.filter((v: any) => (v.status || 'active') === statusFilter)
    if (search) {
      const q = search.toLowerCase()
      list = list.filter((v: any) =>
        v.name?.toLowerCase().includes(q) ||
        v.email?.toLowerCase().includes(q) ||
        v.phone?.toLowerCase().includes(q) ||
        v.license_number?.toLowerCase().includes(q)
      )
    }
    const sortMap: Record<string, [string, string]> = {
      'created_at_desc': ['created_at', 'desc'],
      'created_at_asc': ['created_at', 'asc'],
      'name_asc': ['name', 'asc'],
      'name_desc': ['name', 'desc'],
    }
    const [field, dir] = sortMap[sort] || ['created_at', 'desc']
    list.sort((a: any, b: any) => {
      const cmp = (a[field] || '') < (b[field] || '') ? -1 : (a[field] || '') > (b[field] || '') ? 1 : 0
      return dir === 'desc' ? -cmp : cmp
    })
    return list
  }, [vendors, search, statusFilter, sort])

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--canvas)' }}>
      <div style={{ padding: '24px 28px 0', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 900, color: 'var(--ink-primary)', letterSpacing: '-0.03em', margin: 0 }}>Vendors</h1>
            <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: '3px 0 0' }}>
              {vendors.length} total{filtered.length !== vendors.length ? ` · ${filtered.length} shown` : ''}
            </p>
          </div>
          <button onClick={() => setShowNewModal(true)} className="btn-primary">
            <Plus style={{ width: 15, height: 15 }} /> New vendor
          </button>
        </div>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 20, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, maxWidth: 300 }}>
            <Search style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', width: 13, height: 13, color: 'var(--ink-tertiary)' }} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search vendors..."
              style={{ width: '100%', padding: '9px 12px 9px 32px', background: 'var(--field-bg)', border: '1px solid var(--border-subtle)', borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box' }}
              onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
              onBlur={e => e.currentTarget.style.borderColor = 'var(--border-subtle)'} />
            {search && <button onClick={() => setSearch('')} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0 }}><X style={{ width: 12, height: 12 }} /></button>}
          </div>

          <div style={{ display: 'flex', gap: 6 }}>
            {STATUS_FILTERS.map(s => (
              <button key={s} onClick={() => setStatusFilter(s)}
                style={{ padding: '7px 13px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize', background: statusFilter === s ? 'var(--accent)' : 'var(--hover-bg)', color: statusFilter === s ? 'var(--accent-ink)' : 'var(--ink-tertiary)', border: statusFilter === s ? 'none' : '1px solid var(--border-subtle)' }}>
                {s === 'all' ? 'All' : s.replace('-', ' ')}
              </button>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 6, marginLeft: 'auto', alignItems: 'center' }}>
            <div style={{ display: 'flex', background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 8, overflow: 'hidden' }}>
              {(['grid', 'list'] as const).map(v => (
                <button key={v} onClick={() => setView(v)}
                  style={{ padding: '6px 12px', background: view === v ? 'var(--accent)' : 'transparent', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: view === v ? 'var(--accent-ink)' : 'var(--ink-tertiary)', transition: 'all 150ms ease' }}>
                  {v === 'grid' ? '⊞' : '☰'}
                </button>
              ))}
            </div>

            <div style={{ position: 'relative' }}>
              <button onClick={() => setShowSortMenu(!showSortMenu)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 8, fontSize: 12, color: 'var(--ink-secondary)', cursor: 'pointer' }}>
                <ArrowUpDown style={{ width: 12, height: 12 }} />
                {SORT_OPTIONS.find(o => o.value === sort)?.label}
              </button>
              <AnimatePresence>
                {showSortMenu && (
                  <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 4 }}
                    style={{ position: 'absolute', right: 0, top: '100%', marginTop: 6, background: 'var(--surface-raised)', border: '1px solid var(--border-default)', borderRadius: 12, overflow: 'hidden', zIndex: 50, minWidth: 180 }}>
                    {SORT_OPTIONS.map(opt => (
                      <button key={opt.value} onClick={() => { setSort(opt.value); setShowSortMenu(false) }}
                        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: sort === opt.value ? 'var(--accent-text)' : 'var(--ink-secondary)', textAlign: 'left' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-bg)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                        {opt.label}
                        {sort === opt.value && <Check style={{ width: 12, height: 12 }} />}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '0 28px 28px' }}>
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--ink-tertiary)' }}>Loading...</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 24px' }}>
            <Wrench style={{ width: 36, height: 36, color: 'var(--ink-tertiary)', margin: '0 auto 12px', display: 'block' }} />
            <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink-secondary)', margin: '0 0 6px' }}>
              {search || statusFilter !== 'all' ? 'No vendors match your filters' : 'No vendors yet'}
            </p>
            <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: '0 0 16px' }}>
              {search || statusFilter !== 'all' ? 'Try adjusting your filters' : 'Add your first vendor to track subcontractors and service providers'}
            </p>
            {!search && statusFilter === 'all' && (
              <button onClick={() => setShowNewModal(true)} className="btn-primary" style={{ margin: '0 auto' }}>
                <Plus style={{ width: 14, height: 14 }} /> New vendor
              </button>
            )}
          </div>
        ) : view === 'grid' ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
            {filtered.map((vendor: any) => (
              <VendorCard
                key={vendor.id}
                vendor={vendor}
                serviceLineCount={getServiceLineCount(vendor)}
                onClick={() => navigate(`/app/vendors/${vendor.id}`)}
                onDelete={() => { if (window.confirm(`Delete ${vendor.name}?`)) deleteMutation.mutate(vendor.id) }}
              />
            ))}
          </div>
        ) : (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 14, overflow: 'hidden' }}>
            {/* Column header — desktop only. Cards on mobile are self-labeling. */}
            {!isMobile && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px 110px 100px 130px 100px 80px 40px', padding: '10px 16px', borderBottom: '1px solid var(--border-subtle)', background: 'var(--surface)' }}>
              {['Vendor', 'Status', 'Terms', 'License', 'Insurance expires', 'Service lines', 'Updated', ''].map((h, i) => (
                <div key={i} style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{h}</div>
              ))}
            </div>
            )}
            {filtered.map((vendor: any, i: number) => {
              const sb = STATUS_BADGE[vendor.status || 'active'] || STATUS_BADGE.active
              const insExpiresAt = vendor.insurance_expires_at ? new Date(vendor.insurance_expires_at) : null
              const insExpired = insExpiresAt && insExpiresAt < new Date()
              const serviceLineCount = getServiceLineCount(vendor)
              const isLast = i === filtered.length - 1

              // Mobile card. Compliance-critical fields (license, insurance
              // expiry) preserved with their original red-when-expired
              // coloring. Service-lines count + payment terms grouped as a
              // single secondary line. Delete affordance in the header
              // alongside the status badge.
              if (isMobile) {
                return (
                  <motion.div key={vendor.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: Math.min(i * 0.02, 0.1) }}
                    onClick={() => navigate(`/app/vendors/${vendor.id}`)}
                    style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '14px 16px', borderBottom: isLast ? 'none' : '1px solid var(--border-subtle)', cursor: 'pointer', transition: 'background 150ms ease' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-bg)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <Avatar name={vendor.name} size={36} variant="company" />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-primary)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{vendor.name}</p>
                        {vendor.email && <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: '2px 0 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{vendor.email}</p>}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, alignSelf: 'flex-start', flexShrink: 0 }}>
                        <span style={{ fontSize: 11, fontWeight: 600, color: sb.color, background: sb.bg, padding: '2px 8px', borderRadius: 100 }}>{sb.label}</span>
                        <button onClick={e => { e.stopPropagation(); if (window.confirm(`Delete ${vendor.name}?`)) deleteMutation.mutate(vendor.id) }}
                          aria-label={`Delete ${vendor.name}`}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 4, display: 'flex', borderRadius: 6 }}>
                          <Trash2 style={{ width: 14, height: 14 }} />
                        </button>
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      <span style={{ fontSize: 13, color: 'var(--ink-secondary)' }}>
                        {serviceLineCount} {serviceLineCount === 1 ? 'service line' : 'service lines'} · {PAYMENT_TERM_LABEL[vendor.payment_terms] || vendor.payment_terms || '—'}
                      </span>
                      {(vendor.license_number || vendor.insurance_expires_at) && (
                        <span style={{ fontSize: 12, color: 'var(--ink-tertiary)' }}>
                          {vendor.license_number && <>License {vendor.license_number}</>}
                          {vendor.license_number && vendor.insurance_expires_at && ' · '}
                          {vendor.insurance_expires_at && (
                            <span style={{ color: insExpired ? 'var(--danger)' : 'var(--ink-tertiary)', fontWeight: insExpired ? 600 : 400 }}>
                              Insurance {insExpired ? 'expired ' : 'expires '}{vendor.insurance_expires_at}
                            </span>
                          )}
                        </span>
                      )}
                      <span style={{ fontSize: 11, color: 'var(--ink-tertiary)', marginTop: 2 }}>
                        Updated {formatRelativeDate(vendor.updated_at)}
                      </span>
                    </div>
                  </motion.div>
                )
              }

              // Desktop grid row — unchanged.
              return (
                <motion.div key={vendor.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: Math.min(i * 0.02, 0.1) }}
                  onClick={() => navigate(`/app/vendors/${vendor.id}`)}
                  style={{ display: 'grid', gridTemplateColumns: '1fr 100px 110px 100px 130px 100px 80px 40px', padding: '12px 16px', borderBottom: i < filtered.length - 1 ? '1px solid var(--border-subtle)' : 'none', alignItems: 'center', cursor: 'pointer', transition: 'background 150ms ease' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-bg)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Avatar name={vendor.name} size={30} variant="company" />
                    <div>
                      <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)', margin: 0 }}>{vendor.name}</p>
                      {vendor.email && <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: 0 }}>{vendor.email}</p>}
                    </div>
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 600, color: sb.color, background: sb.bg, padding: '2px 8px', borderRadius: 100, width: 'fit-content' }}>{sb.label}</span>
                  <span style={{ fontSize: 12, color: 'var(--ink-secondary)' }}>{PAYMENT_TERM_LABEL[vendor.payment_terms] || vendor.payment_terms}</span>
                  <span style={{ fontSize: 12, color: 'var(--ink-secondary)' }}>{vendor.license_number || '—'}</span>
                  <span style={{ fontSize: 12, color: insExpired ? 'var(--danger)' : 'var(--ink-secondary)' }}>{vendor.insurance_expires_at || '—'}</span>
                  <span style={{ fontSize: 12, color: 'var(--ink-secondary)' }}>{serviceLineCount}</span>
                  <span style={{ fontSize: 11, color: 'var(--ink-tertiary)' }}>{formatRelativeDate(vendor.updated_at)}</span>
                  <button onClick={e => { e.stopPropagation(); if (window.confirm(`Delete ${vendor.name}?`)) deleteMutation.mutate(vendor.id) }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 4, display: 'flex', borderRadius: 6 }}
                    onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
                    onMouseLeave={e => e.currentTarget.style.color = 'var(--ink-tertiary)'}>
                    <Trash2 style={{ width: 13, height: 13 }} />
                  </button>
                </motion.div>
              )
            })}
          </div>
        )}
      </div>

      <AnimatePresence>
        {showNewModal && (
          <NewVendorModal
            onClose={() => setShowNewModal(false)}
            onCreated={() => queryClient.invalidateQueries({ queryKey: ['vendors'] })}
          />
        )}
      </AnimatePresence>
    </div>
  )
}
